<template>
  <div>
    <div class="columns">
      <div class="column is-full-mobile is-6 ml-auto">
        <neyos-tracker-search />
      </div>
    </div>
    <div class="tile is-ancestor">
      <div class="tile is-parent">
        <div class="tile is-child card">
          <div class="card-header">
            <h2 class="card-header-title">Informations</h2>
            <div class="my-auto mx-3 is-flex is-align-items-center">
              <b-tooltip
                :label="wears.find((wear) => wear.value === neyosTracker.wear).label"
                type="is-light"
                position="is-bottom"
              >
                <b-icon
                  :icon="wearIcons[neyosTracker.wear].icon"
                  :type="wearIcons[neyosTracker.wear].type"
                />
              </b-tooltip>
              <b-tag :type="tags[neyosTracker.availability].type" class="ml-2">
                {{ tags[neyosTracker.availability].label }}
              </b-tag>
            </div>
          </div>
          <div class="card-content">
            <table class="table is-striped is-fullwidth">
              <tr>
                <th>Numéro</th>
                <td>{{ neyosTracker.number }}</td>
              </tr>
              <tr>
                <th>Serial</th>
                <td>{{ neyosTracker.serial }}</td>
              </tr>
              <tr>
                <th>Locations depuis l'achat</th>
                <td>{{ lives.length }}</td>
              </tr>
              <tr>
                <th>Location sur l'année glissante</th>
                <td>{{ totalLivesSinceLastYear }}</td>
              </tr>
              <tr>
                <th>Dernier point relevé</th>
                <td>
                  <span v-if="showLastKnownLocationInformations">{{
                    formatDateWithTime(lastKnownLocation?.locatedAt ?? '')
                  }}</span>
                  <span v-else>–</span>
                </td>
              </tr>
              <tr>
                <th>Dernier niveau de batterie</th>
                <td>
                  <o-battery-level-indicator
                    v-if="showLastKnownLocationInformations"
                    :battery-percentage="lastKnownLocation.batteryPercentage"
                  />
                  <span v-else>–</span>
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>
      <div class="tile is-parent">
        <div class="tile is-child card">
          <neyos-tracker-map :last-known-location="lastKnownLocation" class="card" />
        </div>
      </div>
    </div>
    <div class="tile is-ancestor">
      <div class="tile is-parent">
        <div class="tile is-child card">
          <div class="card-header">
            <h2 class="card-header-title">Changement d'état</h2>
          </div>
          <div class="card-content">
            <neyos-tracker-edit-form
              :neyos-tracker="neyosTracker"
              @update:neyosTracker="$emit('update:neyosTracker', $event)"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="tile is-ancestor">
      <div class="tile is-parent">
        <div class="tile is-child card">
          <div class="card-header">
            <h2 class="card-header-title">Lives</h2>
          </div>
          <div class="card-content">
            <neyos-tracker-live-table :lives="formattedLives" :loading="loading" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { format, isWithinInterval, subYears } from 'date-fns'
import NeyosTrackerMap from './NeyosTrackerMap.vue'
import NeyosTrackerSearch from './NeyosTrackerSearch.vue'
import NeyosTrackerEditForm from './NeyosTrackerEditForm.vue'
import NeyosTrackerLiveTable from './NeyosTrackerLiveTable.vue'
import { TAGS } from '@constants/tracker/tag'
import { WEARS, WEAR_ICONS } from '@constants/tracker/wear'
import OBatteryLevelIndicator from '@components/BatteryLevelIndicator.vue'

export default {
  name: 'NeyosTrackerInformation',

  components: {
    OBatteryLevelIndicator,
    NeyosTrackerMap,
    NeyosTrackerSearch,
    NeyosTrackerEditForm,
    NeyosTrackerLiveTable,
  },

  props: {
    neyosTracker: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      lives: [],
      liveVehicles: {},
      lastKnownLocation: null,
    }
  },

  created() {
    this.tags = TAGS
    this.wears = WEARS
    this.wearIcons = WEAR_ICONS
  },

  watch: {
    neyosTracker: {
      handler() {
        this.load()
      },
      immediate: true,
    },
  },

  computed: {
    showLastKnownLocationInformations() {
      return this.lastKnownLocation !== null
    },

    totalLivesSinceLastYear() {
      const now = new Date()
      const oneYearAgo = subYears(now, 1)

      return this.lives.filter(
        (live) =>
          isWithinInterval(live.startedAt, { start: oneYearAgo, end: now }) ||
          isWithinInterval(live.endedAt, { start: oneYearAgo, end: now }) ||
          isWithinInterval(now, { start: live.startedAt, end: live.endedAt }),
      ).length
    },

    formattedLives() {
      return this.lives.map((live) => ({
        ...live,
        vehicleId: this.liveVehicles[live.id]?.id,
        vehicleNumber: this.liveVehicles[live.id]?.number,
      }))
    },
  },

  methods: {
    async load() {
      this.loading = true

      await Promise.all([
        this.fetchAllLivesByNeyosTracker(),
        this.fetchLiveVehiclesByNeyosTracker(),
        this.fetchTrackerLastKnownLocation(),
      ])

      this.loading = false
    },

    async fetchAllLivesByNeyosTracker() {
      try {
        this.lives = await this.$services.liveService.getAllByNeyosTrackerId(this.neyosTracker.id)
      } catch {
        this.lives = []
      }
    },

    async fetchLiveVehiclesByNeyosTracker() {
      try {
        this.liveVehicles = (
          await this.$services.liveVehicleService.getAllByNeyosTrackerId(this.neyosTracker.id)
        ).reduce(
          (vehicles, vehicle) => ({
            ...vehicles,
            [vehicle.liveId]: { ...vehicle },
          }),
          {},
        )
      } catch {
        this.liveVehicles = {}
      }
    },

    async fetchTrackerLastKnownLocation() {
      try {
        this.lastKnownLocation = await this.$services.neyosTrackerLocationService.getLatestLocation(
          this.neyosTracker.id,
        )
      } catch {
        this.lastKnownLocation = null
      }
    },

    formatDateWithTime(date) {
      return !date ? 'Aucune date' : format(date, 'dd/MM/yyyy à HH:mm')
    },
  },
}
</script>
